import React from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, ArrowRight, ShoppingBag } from "lucide-react";

export default function EventFeed({ events = [], merchItems = [], loading = false }) {
  const now = new Date();

  const orderedEvents = [...events].reverse();

  const upcomingEvents = orderedEvents.filter(
    (event) => new Date(event.dateTime) >= now
  );

  const pastEvents = orderedEvents.filter(
    (event) => new Date(event.dateTime) < now
  );

  function formatEventDate(dateString) {
    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) return dateString;

    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  }

  function getLinkedMerch(event) {
    const eventId = event?._id || event?.id;

    return merchItems.filter((item) => {
      const associatedEvents = Array.isArray(item.associatedEvents)
        ? item.associatedEvents
        : [];
      const singleEventId = item.eventId ? [item.eventId] : [];
      const linkedIds = [...associatedEvents, ...singleEventId].filter(Boolean);

      return linkedIds.includes(eventId);
    });
  }

  function EventCard({ event, isPast = false }) {
    const linkedMerch = getLinkedMerch(event);

    return (
      <Link
        to={`/events/${event.customUrl || event._id || event.id}`}
        key={event._id}
        className="group block rounded-3xl focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-200"
      >
        <article
          className={`overflow-hidden rounded-3xl border bg-white shadow-sm transition-all duration-300 ${
            isPast
              ? "border-slate-200/80 opacity-90 hover:opacity-100 hover:shadow-lg"
              : "border-slate-200/80 hover:-translate-y-1.5 hover:border-slate-300 hover:shadow-2xl"
          }`}
        >
          <div className="relative overflow-hidden">
            <div className="relative h-64 bg-slate-100">
              <img
                src={event.image}
                alt={event.name}
                className={`absolute inset-0 h-full w-full object-cover transition-transform duration-700 ${
                  isPast ? "group-hover:scale-105" : "group-hover:scale-110"
                }`}
                onError={(e) => {
                  e.currentTarget.src =
                    "https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1200&q=80";
                }}
              />

              <div
                className={`absolute inset-0 ${
                  isPast
                    ? "bg-gradient-to-t from-slate-950/70 via-slate-900/20 to-transparent"
                    : "bg-gradient-to-t from-slate-950/60 via-slate-900/10 to-transparent"
                }`}
              />

              <div className="absolute left-4 top-4 z-10 flex flex-wrap gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold shadow-sm backdrop-blur-sm ${
                    isPast
                      ? "bg-red-600 text-white"
                      : "bg-white/90 text-slate-800"
                  }`}
                >
                  {isPast ? "Past Event" : "Upcoming"}
                </span>

                {linkedMerch.length > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/70 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                    <ShoppingBag className="h-3.5 w-3.5" />
                    {linkedMerch.length} merch item{linkedMerch.length === 1 ? "" : "s"}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="p-5">
            <div className="mb-3">
              <h2 className="line-clamp-2 text-xl font-semibold leading-tight tracking-tight text-slate-900">
                {event.name}
              </h2>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3 text-sm text-slate-600">
                <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <p className="line-clamp-2">{formatEventDate(event.dateTime)}</p>
              </div>

              <div className="flex items-start gap-3 text-sm text-slate-600">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <p className="line-clamp-2">{event.location}</p>
              </div>
            </div>

            {linkedMerch.length > 0 && (
              <div className="mt-5 rounded-2xl bg-slate-50 p-4">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <ShoppingBag className="h-3.5 w-3.5" />
                  Linked merch
                </div>
                <p className="mt-2 line-clamp-2 text-sm text-slate-700">
                  {linkedMerch
                    .slice(0, 2)
                    .map((item) => item.title || item.name)
                    .join(" • ")}
                  {linkedMerch.length > 2 ? ` +${linkedMerch.length - 2} more` : ""}
                </p>
              </div>
            )}

            <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
              <span className="text-sm font-medium text-slate-900 transition-colors group-hover:text-slate-700">
                View details
              </span>
              <ArrowRight className="h-4 w-4 text-slate-400 transition-all duration-300 group-hover:translate-x-1 group-hover:text-slate-600" />
            </div>
          </div>
        </article>
      </Link>
    );
  }

  function Section({ title, description, items, isPast = false }) {
    if (!items.length) return null;

    return (
      <section className="mt-12 first:mt-0">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
              {title}
            </h2>
            <p className="mt-1 text-sm text-slate-500">{description}</p>
          </div>

          <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-600">
            {items.length}
          </span>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((event) => (
            <EventCard key={event._id} event={event} isPast={isPast} />
          ))}
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-10">
          <div className="h-8 w-48 animate-pulse rounded-lg bg-slate-200" />
          <div className="mt-3 h-4 w-80 animate-pulse rounded-lg bg-slate-100" />
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
            >
              <div className="h-64 animate-pulse bg-slate-200" />
              <div className="space-y-4 p-5">
                <div className="h-6 w-3/4 animate-pulse rounded bg-slate-200" />
                <div className="h-4 w-2/3 animate-pulse rounded bg-slate-100" />
                <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
                <div className="h-4 w-24 animate-pulse rounded bg-slate-100" />
              </div>
            </div>
          ))}
        </div>
      </main>
    );
  }

  if (!orderedEvents.length) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-slate-200 bg-white px-8 py-16 text-center shadow-sm">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Events
          </h1>
          <p className="mt-3 text-sm text-slate-500">
            Browse upcoming and past community events.
          </p>

          <div className="mx-auto mt-8 max-w-md rounded-2xl bg-slate-50 px-6 py-10">
            <h2 className="text-lg font-medium text-slate-900">No events yet</h2>
            <p className="mt-2 text-sm text-slate-500">
              There are no events to show right now. Check back soon for new
              announcements.
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <Section
        title="Upcoming Events"
        description="Stay up to date with what’s coming next."
        items={upcomingEvents}
      />

      <Section
        title="Past Events"
        description="A look back at previous events and gatherings."
        items={pastEvents}
        isPast
      />
    </main>
  );
}
